'use client'

import NextLink from 'next/link'
import { useContext, type MouseEvent } from 'react'
import { Button } from '@shc/ui'
import type { AppointmentDate } from '@lib/types'
import type { TypePlacePublic } from '@lib/location-types'
import { useState } from 'react'
import useAnalytics, { type SPContext } from '@hooks/use-analytics'
import LocationSaveASpot from '@app/[locale]/(main)/locations/_components/location-save-a-spot'
import { type SearchNamespace } from '@app/[locale]/(main)/search/_components/search-session'
import { routeMapper } from '@lib/mappers/menu-item-mapper'
import NonSRSLabModal from '@components/non-srs-lab-modal'
import { AnalyticsLink } from '@components/analytics'
import {
  BlockContext,
  ItemContext,
  PageContext,
  PlaceContext,
  ProviderContext,
} from '@lib/analytics'

enum SchedulingType {
  EPICOPENSCHEDULING = 'EPIC OPEN SCHEDULING',
  CTAONLY = 'CTA ONLY',
}

export type LocationSchedulingDetailProps = {
  type: TypePlacePublic['scheduling_type']
  placeId: TypePlacePublic['id']
  description?: TypePlacePublic['scheduling_description']
  schedulingUrl?: TypePlacePublic['scheduling_cta_url']
  ctaLabel?: TypePlacePublic['scheduling_cta_label']
  firstAvailableAppointment: AppointmentDate | string | null
  isSRS?: boolean
  placeType?: TypePlacePublic['place_type_name']
}

const LocationSchedulingDetail = ({
  type,
  placeId,
  description,
  schedulingUrl,
  ctaLabel,
  firstAvailableAppointment,
  isSRS,
  placeType,
}: LocationSchedulingDetailProps) => {
  const [nonSRSModalOpen, setNonSRSModalOpen] = useState(false)
  const uppercasedType = type?.toUpperCase()
  let schedulingDetails = undefined
  const isEpicOpenScheduling = uppercasedType === SchedulingType.EPICOPENSCHEDULING
  const isCTAOnly = uppercasedType === SchedulingType.CTAONLY
  const isNonSrsLab = !isSRS && placeType === 'Lab'

  // Analytics tracking vars
  const { trackSnowplow } = useAnalytics()
  const objectId = placeId?.toString() ?? '-1'
  const namespace: SearchNamespace = 'places'

  // Environment contexts
  const pageContext = useContext(PageContext)
  const placeContext = useContext(PlaceContext)
  const providerContext = useContext(ProviderContext)
  const blockContext = useContext(BlockContext)
  const itemContext = useContext(ItemContext)
  const environmentContexts: SPContext[] = [
    pageContext,
    placeContext,
    providerContext,
    blockContext,
    itemContext,
  ].filter((context) => context !== null)

  // checking for scheduling type
  if (isEpicOpenScheduling) {
    schedulingDetails = (
      <LocationSaveASpot
        firstAvailableAppointment={firstAvailableAppointment}
        description={description}
        ctaLabel={ctaLabel}
        schedulingUrl={schedulingUrl}
        placeId={placeId}
      />
    )
  }

  // checking for scheduling type
  if (isCTAOnly) {
    const link = routeMapper(undefined, schedulingUrl ?? '')

    const handleNonSrsButtonClick = (e: MouseEvent<any>) => {
      // button click event
      trackSnowplow({
        event: { name: 'component_click', data: {} },
        contexts: [
          {
            name: 'component',
            data: {
              component_text: e.currentTarget.textContent,
            },
          },
          ...environmentContexts,
        ],
      })

      // modal open event
      trackSnowplow({
        event: {
          name: 'modal_open',
          data: {},
        },
        contexts: [{ name: 'modal', data: { modal_name: 'NonSrsModal' } }, ...environmentContexts],
      })

      setNonSRSModalOpen(true)
    }

    schedulingDetails = (
      <>
        {description && <div>{description}</div>}
        {schedulingUrl &&
          (isNonSrsLab ? (
            // Non-SRS Lab button -> open modal
            <Button
              as="button"
              id="scheduling-button-page"
              size="md"
              width="lg"
              data-testid="scheduling-button-modal"
              onClick={handleNonSrsButtonClick}
            >
              {ctaLabel}
            </Button>
          ) : (
            // SRS Lab button -> link to external booking
            <AnalyticsLink
              as={Button}
              asPassthru={NextLink}
              id="scheduling-button-page"
              href={link.route}
              target={link.isInternal ? undefined : '_blank'}
              rel={link.isInternal ? undefined : 'noopener noreferrer'}
              size="md"
              width="lg"
              data-testid="scheduling-button"
              algolia={{ conversion: { namespace, objectId } }}
            >
              {ctaLabel}
            </AnalyticsLink>
          ))}
      </>
    )
  }

  return (
    <>
      {schedulingDetails}
      <NonSRSLabModal
        isModalOpen={nonSRSModalOpen}
        setIsModalOpen={setNonSRSModalOpen}
        url={schedulingUrl}
        placeId={placeId}
      />
    </>
  )
}

export default LocationSchedulingDetail
